import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import {
  RestPropertyManagerAccessModel,
  RestPropertyModel,
  RestPropertyTurnsWithoutAssignees,
  UpdatePropertyManagerModel,
} from '@shared/interfaces/rest-portfolio-model.interface';
import { PreleaseFilterMode } from '@shared/interfaces/turnover.interface';

@Injectable({
  providedIn: 'root',
})
export class PropertiesService {
  constructor(private restApiService: RestapiService) {}

  update(propertyDetails: RestPropertyModel): Observable<RestPropertyModel> {
    return this.restApiService.update<RestPropertyModel>(`Properties/${propertyDetails.id}`, propertyDetails);
  }

  getAllUserProperties(): Observable<RestPropertyModel[]> {
    return this.restApiService.getData<RestPropertyModel[]>(`properties`);
  }

  getAllUserUnassignedProperties(): Observable<RestPropertyModel[]> {
    return this.restApiService.getData<RestPropertyModel[]>(`properties/unassigned`);
  }

  getPropertyManagers(): Observable<RestPropertyManagerAccessModel[]> {
    return this.restApiService.getData<RestPropertyManagerAccessModel[]>(`Properties/PropertyManagers`);
  }

  getPropertyManagersForProperty(propertyId: number): Observable<RestPropertyManagerAccessModel[]> {
    return this.restApiService.getData<RestPropertyManagerAccessModel[]>(`Properties/${propertyId}/PropertyManagers`);
  }

  removePropertyManagers(propertyManagerModels: UpdatePropertyManagerModel[]): Observable<boolean> {
    return this.restApiService.update(`Properties/RemovePropertyManagers`, propertyManagerModels);
  }

  setPropertyManagers(propertyManagerModels: UpdatePropertyManagerModel[]): Observable<boolean> {
    return this.restApiService.update(`Properties/SetPropertyManagers`, propertyManagerModels);
  }

  setPropertyPreleaseFilter(propertyId: number, preleaseFilterMode: PreleaseFilterMode): Observable<void> {
    return this.restApiService.create(`Properties/${propertyId}/SetPreleaseMode`, preleaseFilterMode);
  }

  addPropertyToPortfolio(portfolioId: number, propertyName: string): Observable<RestPropertyModel> {
    return this.restApiService.create(`Properties`, {
      portfolioId,
      name: propertyName,
    });
  }

  getTurnsWithoutAssignees(
    propertyId: number,
    step: WorkflowStepEnumType
  ): Observable<RestPropertyTurnsWithoutAssignees> {
    return this.restApiService.getData<RestPropertyTurnsWithoutAssignees>(
      `Properties/turnsWithoutAssignee/${propertyId}/${step}`
    );
  }

  getTurnsWithoutPortfolioAssignees(
    portfolioId: number,
    step: WorkflowStepEnumType
  ): Observable<RestPropertyTurnsWithoutAssignees> {
    return this.restApiService.getData<RestPropertyTurnsWithoutAssignees>(
      `Portfolio/turnsWithoutAssignee/${portfolioId}/${step}`
    );
  }

  deletePropertyManagers(propertyManagers: { propertyId: number; userId: number }[]) {
    return this.restApiService.update<RestPropertyTurnsWithoutAssignees>(
      `Properties/RemovePropertyManagers`,
      propertyManagers
    );
  }

  updatePropertyManagers(propertyManagers: { propertyId: number; userId: number }[]) {
    return this.restApiService.update<RestPropertyTurnsWithoutAssignees>(
      `Properties/SetPropertyManagers`,
      propertyManagers
    );
  }

  updateProperty(property: RestPropertyModel): Observable<void> {
    return this.restApiService.update(`Properties/${property.id}`, property);
  }
}
